import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createWoodsScene } from "../../lib/woodsScene";
import { audio } from "../../lib/audio";
import { useIdle } from "./useIdle";
import { imageSrc } from "../../lib/api";

const NEXT = { home: ["bloom", 5200], clear: ["bloom", 1800], bloom: ["painting", 1500], painting: ["done", 3600] };

export default function WoodsExperience({ painting, onDone }) {
  const withHouse = painting.slug === "woods";
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const lastRustle = useRef(0);
  const [stage, setStage] = useState("forest");
  const [touched, setTouched] = useState(false);
  const idle = useIdle(8000);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const scene = createWoodsScene(canvasRef.current, {
      onProgress: setProgress,
      onCleared: () => setStage(withHouse ? "home" : "clear"),
      onRustle: (k) => {
        const now = performance.now();
        if (now - lastRustle.current > 110) {
          lastRustle.current = now;
          audio.rustle(k);
        }
      },
    });
    sceneRef.current = scene;
    return () => scene.destroy();
  }, [withHouse]);

  useEffect(() => {
    if (stage === "home" || stage === "clear") {
      sceneRef.current?.goHome(stage === "home");
      audio.gust();
    }
    const next = NEXT[stage];
    if (!next) return;
    const id = setTimeout(() => (next[0] === "done" ? onDone() : setStage(next[0])), next[1]);
    return () => clearTimeout(id);
  }, [stage, onDone]);

  const down = (e) => {
    setTouched(true);
    sceneRef.current?.pointerDown(e.clientX, e.clientY);
  };
  const move = (e) => sceneRef.current?.pointerMove(e.clientX, e.clientY, e.buttons > 0 || e.pointerType === "touch");

  return (
    <div data-testid="woods-experience" data-stage={stage} className="fixed inset-0 z-50 overflow-hidden bg-[#070C0A]">
      <canvas
        ref={canvasRef}
        data-testid="experience-leaves-canvas"
        className="absolute inset-0 h-full w-full cursor-grab touch-none active:cursor-grabbing"
        onPointerDown={down}
        onPointerMove={move}
      />
      <AnimatePresence>
        {(!touched || idle) && stage === "forest" && (
          <motion.p
            key="hint"
            data-testid="play-hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, delay: 1.2 }}
            className="pointer-events-none absolute inset-x-0 bottom-24 text-center font-display text-2xl italic text-[#2D1E3E]/85 drop-shadow-[0_2px_10px_rgba(255,255,255,0.8)] sm:text-3xl"
          >
            brush the leaves aside
          </motion.p>
        )}
        {stage === "home" && (
          <motion.p
            key="home"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 2, delay: 1.6 }}
            className="pointer-events-none absolute inset-x-0 bottom-24 text-center font-display text-3xl italic text-[#B5579A]"
          >
            home
          </motion.p>
        )}
      </AnimatePresence>
      {stage === "forest" && (
        <div className="pointer-events-none absolute bottom-10 left-1/2 h-px w-40 -translate-x-1/2 bg-white/10">
          <div data-testid="woods-progress" className="h-full bg-[#7E57C2]/70 transition-[width] duration-300" style={{ width: `${Math.min(100, (progress / 0.7) * 100)}%` }} />
        </div>
      )}
      <AnimatePresence>
        {(stage === "bloom" || stage === "painting") && (
          <motion.div
            key="bloom"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4 }}
            className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,#ffffff_0%,#fff3d6_40%,#f6d9ec_100%)]"
          />
        )}
      </AnimatePresence>
      {stage === "painting" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.6 }}
          className="absolute inset-0 flex items-center justify-center bg-[#F3E8FF] p-8"
        >
          <motion.img
            data-testid="woods-reveal-image"
            src={imageSrc(painting.image_url)}
            alt={painting.title}
            initial={{ opacity: 0, scale: 1.06, filter: "blur(14px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 2.6, ease: "easeOut" }}
            className="h-auto max-h-[80vh] w-auto max-w-[88vw] shadow-[0_30px_80px_rgba(91,58,133,0.22)]"
          />
        </motion.div>
      )}
    </div>
  );
}
